import React, { useContext, useState } from 'react';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { t } from '../../../../../../types/translation/Translator';
import ToggleButton from '../../../../Common/ToggleButton';
import QuickActionBreakView from './QuickActionBreakView';
import QuickActionBundleView from './QuickActionBundleView';
import { HandlingUnitContext } from '../../../../../../context/HandlingUnitContext';
import { DefaultDropdownItem, DropdownSearchSelect } from '../../../../../components/Dropdown';
import { ProductMasterDataContext } from '../../../../../../context/ProductMasterDataContext';
import ActionModal from '../../../../../components/Modal/templates/ActionModal';
import { Divider } from '../../../../../components/Divider';
import QuickActionDetailView from './QuickActionDetailView';
import QuickActionTransactionView from './QuickActionTransactionView';
import { ProcessTransactionProps } from '../../../../Operation/Order/Modals/Panes/Views/ProcessOrderTransactionDefaultBody';
import { CompanyContext } from '../../../../../../context/CompanyContext';

enum QuickActionView {
  detail = 'detail',
  transaction = 'transaction',
  break = 'break',
  bundle = 'bundle',
}

function quickActionViewToLocalizedString(view: string) {
  switch (view) {
    case QuickActionView.transaction:
      return t(TranslationKey.transaction).singular.label;
    case QuickActionView.bundle:
      return t(TranslationKey.handlingUnitBundle).singular.label;
    case QuickActionView.break:
      return t(TranslationKey.handlingUnitBreak).singular.label;
    case QuickActionView.detail:
      return t(TranslationKey.details).singular.label;
  }

  return '';
}

interface QuickActionModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  pmdId?: string;
  productId?: string;

  props: ProcessTransactionProps;
}

export default function QuickActionModal({ open, setOpen, pmdId, productId, props }: QuickActionModalProps) {
  if (!open) return null;

  const { breakPedigrees, bundlePedigrees } = useContext(HandlingUnitContext);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { currentCompany } = useContext(CompanyContext);

  const [pmd, setPmd] = useState(productMasterData.get(pmdId || 'NONE'));
  const [view, setView] = useState<QuickActionView>(
    pmd?.kitManaged && productId ? QuickActionView.detail : QuickActionView.transaction,
  );

  const Content = () => {
    if (!pmd) return null;
    switch (view) {
      case QuickActionView.transaction:
        return <QuickActionTransactionView setOpen={setOpen} pmd={pmd} productId={productId} props={props} />;
      case QuickActionView.break:
        return <QuickActionBreakView setOpen={setOpen} pmdId={pmd.id} productId={productId} />;
      case QuickActionView.bundle:
        return <QuickActionBundleView setOpen={setOpen} pmdId={pmd.id} productId={productId} />;
      case QuickActionView.detail:
        return <QuickActionDetailView setOpen={setOpen} pmdId={pmd.id} productId={productId} />;
    }
  };

  const views = Object.values(QuickActionView).filter(
    key =>
      (key === QuickActionView.break && breakPedigrees.get(pmd?.id || 'NONE')?.length) ||
      (key === QuickActionView.bundle && bundlePedigrees.get(pmd?.id || 'NONE')?.length) ||
      (key === QuickActionView.detail && pmd?.kitManaged) ||
      key === QuickActionView.transaction,
  );

  return (
    <ActionModal
      width='800px'
      title={t(pmdId ? TranslationKey.stockAdjustment : TranslationKey.addProduct).singular.upper}
      open={open}
      setOpen={setOpen}
    >
      <div className='modal-x grid gap-4 pb-6'>
        {pmd && productId && views.length > 1 ? (
          <ToggleButton
            onChange={value => setView(value as QuickActionView)}
            selected={view}
            values={views}
            text={value => quickActionViewToLocalizedString(value)}
          />
        ) : null}

        <DropdownSearchSelect
          disabled={!!pmdId}
          label={t(TranslationKey.product).singular.upper}
          placeholder={t(TranslationKey.selectProduct).singular.upper}
          items={[...productMasterData.values()].filter(p => p.companyId === currentCompany.id)}
          value={pmd}
          onSelect={value => setPmd(value)}
          textBuilder={value => `${value.productName} (${value.productNumber})`}
          itemBuilder={value => <DefaultDropdownItem text={value.productName} subtext={value.productNumber} />}
        />
      </div>
      {pmd && <Divider />}
      <Content />
    </ActionModal>
  );
}
